import { useFlags } from '@atlaskit/flag';
import TextField from '@atlaskit/textfield';
import { IUnsuccessfulDeliveryReason } from 'core/api/meta/meta-api-interface';
import MetaApiService from 'core/api/meta/meta-api.service';
import { ButtonType } from 'core/enums/button-type';
import { useDialog } from 'core/providers/DialogProvider';
import { showErrorFlag, showSuccessFlag } from 'core/utilities/flags-helper';
import { useState } from 'react';
import SharedButton from 'shared/components/buttons/button';
import SharedDialogBase from 'shared/components/dialog-base/dialog-base';
import { ISharedDialogButton } from 'shared/components/dialog-base/dialog-base-interface';
import { v4 as uuid } from 'uuid';

interface IEditUnsuccessfulDeliveryReasonsDialog {
  reasons: IUnsuccessfulDeliveryReason[];
}

const EditUnsuccessfulDeliveryReasonsDialog = ({ reasons }: IEditUnsuccessfulDeliveryReasonsDialog) => {
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [editedReasons, setEditedReasons] = useState<IUnsuccessfulDeliveryReason[]>(reasons);

  const flags = useFlags();
  const dialog = useDialog();

  const submit = async () => {
    try {
      setFormSubmitting(true);
      if (editedReasons.some((reason) => reason.name.trim() === '')) {
        throw new Error('Reason name cannot be empty');
      }
      await MetaApiService.update('deliveries', {
        unsuccessfulDeliveryReasons: editedReasons,
      });
      dialog?.closeDialog();
      showSuccessFlag('Update successful', 'Unsuccessful delivery reasons updated successfully', flags);
    } catch (error) {
      setFormSubmitting(false);
      showErrorFlag('Update failed', 'Unable to update the unsuccessful delivery reasons, please try again', flags);
    }
  };

  const buttons: ISharedDialogButton[] = [
    {
      key: 'close',
      type: ButtonType.Button,
      buttonProps: {
        onClick: () => dialog?.closeDialog(),
        label: 'Cancel',
        appearance: 'subtle',
        type: 'button',
      },
    },
    {
      key: 'submit',
      type: ButtonType.Button,
      buttonProps: {
        isLoading: formSubmitting,
        onClick: submit,
        label: 'Save',
        appearance: 'primary',
        type: 'button',
      },
    },
  ];

  const customContent = () => {
    return (
      <div className='p-4 flex flex-col gap-4'>
        {editedReasons.map((reason) => (
          <div key={reason.key} className='flex items-center gap-2'>
            <TextField
              isDisabled={formSubmitting}
              value={reason.name}
              onChange={(event) => {
                const newValue = event.currentTarget.value;
                setEditedReasons((prev) => prev.map((r) => (r.key === reason.key ? { ...r, name: newValue } : r)));
              }}
            />
            {editedReasons.length > 1 && reason.key !== 'other' && (
              <SharedButton
                disabled={formSubmitting}
                onClick={() => setEditedReasons((prev) => prev.filter((r) => r.key !== reason.key))}
                type='button'
                appearance='danger'
                label='Remove'
              />
            )}
          </div>
        ))}
        <SharedButton
          disabled={formSubmitting}
          onClick={() =>
            setEditedReasons((prev) => {
              prev.splice(prev.length - 1, 0, {
                key: uuid(),
                name: 'New reason',
              });
              return [...prev];
            })
          }
          type='button'
          appearance='primary'
          label='Add Reason'
        />
      </div>
    );
  };

  return (
    <SharedDialogBase
      title='Edit unsuccessful delivery reasons'
      customContentTemplate={customContent()}
      customButtons={buttons}
    />
  );
};

export default EditUnsuccessfulDeliveryReasonsDialog;
